import React, { useState } from "react";
import { Loader2, ShieldCheck, Upload, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const UploadFallback = ({ onDone, testid = "kyc-input" }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/kyc/submit", { file_id: r.data.file_id });
      toast.success("ID submitted for review");
      await onDone?.();
    } catch (e) { toast.error(apiError(e, "Upload failed")); }
    setBusy(false);
  };
  return (
    <label className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold cursor-pointer" data-testid="id-upload-fallback">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Upload className="w-3.5 h-3.5"/>} Upload a photo of your Aadhaar instead
      <input type="file" accept="image/*" className="hidden" disabled={busy} data-testid={testid}
             onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}/>
    </label>
  );
};

// Account-level identity check (Aadhaar OTP). Once approved it is never asked again — in either mode.
export const IdentityVerify = ({ onDone }) => {
  const { user, refresh } = useAuth();
  const [num, setNum] = useState("");
  const [otp, setOtp] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fallback, setFallback] = useState(false);
  const done = async () => { await refresh(); onDone?.(); };

  if (user?.kyc_status === "approved") {
    return <div className="text-sm text-emerald-700 inline-flex items-center gap-1.5" data-testid="identity-verified"><ShieldCheck className="w-4 h-4"/> Identity verified</div>;
  }
  if (user?.kyc_doc_id && user?.kyc_status === "pending") {
    return <div className="text-sm text-sky-800 inline-flex items-center gap-1.5" data-testid="identity-under-review"><Clock className="w-4 h-4"/> Your ID is under review — usually within a few hours.</div>;
  }
  if (!user?.instant_identity) {
    return (
      <div className="space-y-2" data-testid="identity-verify">
        <p className="text-xs text-slate-600">Upload a clear photo of your Aadhaar card. Our team reviews it — usually within a few hours.</p>
        <UploadFallback onDone={done}/>
      </div>
    );
  }
  const start = async () => {
    setBusy(true);
    try { await api.post("/verify/aadhaar/start", { aadhaar_number: num }); setSent(true); toast.success("OTP sent to your Aadhaar-linked mobile"); }
    catch (e) { toast.error(apiError(e, "Couldn't start verification")); if ([502, 503, 504].includes(e?.response?.status)) setFallback(true); }
    setBusy(false);
  };
  const complete = async () => {
    setBusy(true);
    try { await api.post("/verify/aadhaar/complete", { otp }); toast.success("Identity verified"); await done(); }
    catch (e) { toast.error(apiError(e, "Verification failed")); if ([502, 503, 504].includes(e?.response?.status)) setFallback(true); }
    setBusy(false);
  };
  return (
    <div className="space-y-2" data-testid="identity-verify">
      {!sent ? (
        <div className="flex gap-2 flex-wrap">
          <Input data-testid="aadhaar-number-input" inputMode="numeric" maxLength={14} value={num} placeholder="12-digit Aadhaar number"
                 onChange={(e) => setNum(e.target.value.replace(/[^\d ]/g, ""))} className="rounded-xl bg-white max-w-[220px]"/>
          <Button data-testid="aadhaar-send-otp-btn" onClick={start} disabled={busy || num.replace(/\D/g, "").length !== 12} className="rounded-full">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Send OTP"}
          </Button>
        </div>
      ) : (
        <div className="flex gap-2 flex-wrap">
          <Input data-testid="aadhaar-otp-input" inputMode="numeric" maxLength={6} value={otp} placeholder="6-digit OTP"
                 onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} className="rounded-xl bg-white max-w-[160px]"/>
          <Button data-testid="aadhaar-verify-btn" onClick={complete} disabled={busy || otp.length < 4} className="rounded-full">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Verify"}
          </Button>
          <button type="button" className="text-xs underline text-slate-500" onClick={() => { setSent(false); setOtp(""); }}>Change number</button>
        </div>
      )}
      <p className="text-[11px] text-slate-500">We only keep your verified name and the last 4 digits. Your Aadhaar number is never stored.</p>
      {fallback && <UploadFallback onDone={done}/>}
    </div>
  );
};

export default IdentityVerify;
